import type { ReactNode } from "react";
import { Icon } from "./Icon";
import { AcademyParticles } from "./AcademyEffects";
import type { IconName } from "../types/domain";

const highlights = [
  { image: "learners", value: "100K+", label: "Learners" },
  { image: "programs", value: "250+", label: "Programs" },
  { image: "countries", value: "50+", label: "Countries" },
  { image: "satisfaction", value: "98%", label: "Satisfaction" },
];

const features = [
  {
    image: "learning",
    title: "AI-Powered Learning",
    detail: "Personalized pathways",
    href: "https://academy.chatoner.com/programs",
  },
  {
    image: "projects",
    title: "Hands-On Projects",
    detail: "Build real skills",
    href: "https://academy.chatoner.com/programs",
  },
  {
    image: "community",
    title: "Global Community",
    detail: "Learn together",
    href: "https://academy.chatoner.com/events",
  },
  {
    image: "careers",
    title: "Career Opportunities",
    detail: "From learning to impact",
    href: "https://academy.chatoner.com/proof",
  },
];

export function GatewayLanding({
  onSignIn,
  onSignup,
  onApply,
  onInvite,
  savedProgress,
  accessNotes,
}: {
  onSignIn: () => void;
  onSignup: () => void;
  onApply: () => void;
  onInvite: () => void;
  savedProgress: ReactNode;
  accessNotes: ReactNode;
}) {
  const routes: {
    icon: IconName;
    title: string;
    detail: string;
    action: string;
    actionIcon: IconName;
    onClick: () => void;
  }[] = [
    {
      icon: "user",
      title: "Create an account",
      detail: "Begin your family, professional or organization journey.",
      action: "Email verification",
      actionIcon: "mail",
      onClick: onSignup,
    },
    {
      icon: "clipboard",
      title: "Start an application",
      detail: "Choose a program, track and specific available cohort.",
      action: "Cohort choice required",
      actionIcon: "clipboard",
      onClick: onApply,
    },
    {
      icon: "mail",
      title: "Accept an invitation",
      detail: "Join an academy family, relationship, team or staff role.",
      action: "Invitation token",
      actionIcon: "mail",
      onClick: onInvite,
    },
  ];
  return (
    <div className="gateway-landing welcome-page" data-design="academy-portal-reference-v5">
      <div className="gateway-layout">
        <section className="gateway-hero" aria-labelledby="gateway-title">
          <img
            className="gateway-portal-art"
            data-academy-effect="true"
            src="/images/academy/gateway-reference-v2.webp"
            alt=""
            width="1024"
            height="1152"
            fetchPriority="high"
          />
          <AcademyParticles className="gateway-portal-particles" />
          <div className="gateway-hero-copy">
            <p className="gateway-eyebrow">Learn · Create · Belong · Impact</p>
            <h1 id="gateway-title">
              <span>
                Learn <em>Practical AI</em>
              </span>
              <span>Build A Brighter</span>
              <span>
                Human Future <em>Together</em>
              </span>
            </h1>
            <div className="gateway-title-rule" />
            <p className="gateway-intro">
              Gain real-world AI skills, join a global community of learners and creators, and turn your ideas
              into impact — together.
            </p>
            <div className="gateway-hero-actions">
              <a className="gateway-neon-button" href="https://academy.chatoner.com">
                Explore the Academy <Icon name="arrow-right" size={20} />
              </a>
            </div>
          </div>
          <div className="gateway-stats" aria-label="Academy highlights">
            {highlights.map(highlight => (
              <div key={highlight.image}>
                <img
                  className="gateway-stat-art"
                  src={`/images/academy/stat-${highlight.image}-v1.webp`}
                  alt=""
                  width="64"
                  height="64"
                />
                <strong>{highlight.value}</strong>
                <span>{highlight.label}</span>
              </div>
            ))}
          </div>
        </section>
        <section className="gateway-action-panel" aria-labelledby="gateway-action-title">
          <div className="gateway-panel-head">
            <span className="gateway-glow-icon">
              <Icon name="arrow-right" size={34} />
            </span>
            <div>
              <h2 id="gateway-action-title">How would you like to continue?</h2>
              <p>Choose the entry point that matches what you need to do today.</p>
              <span className="gateway-script gateway-motto">Same people. Bigger possibilities.</span>
            </div>
          </div>
          <button type="button" className="gateway-signin" aria-label="Sign in" onClick={onSignIn}>
            <img
              className="gateway-globe"
              data-academy-effect="true"
              src="/images/academy/signin-globe-v2.webp"
              alt=""
              width="1536"
              height="768"
            />
            <span className="gateway-globe-orbit" data-academy-effect="true" aria-hidden="true" />
            <AcademyParticles />
            <img
              className="gateway-lock"
              src="/images/academy/signin-padlock-v3.webp"
              alt=""
              width="971"
              height="1619"
            />
            <span className="gateway-signin-copy">
              <span className="gateway-eyebrow">Returning member</span>
              <strong>Sign in</strong>
              <span className="gateway-signin-description">
                Open the workspace and role already assigned to your verified Academy account.
              </span>
              <span className="gateway-secure">
                <Icon name="shield" size={21} />
                Secure access
              </span>
            </span>
            <span className="gateway-script gateway-globe-motto">
              Learning
              <br />
              today for a brighter
              <br />
              tomorrow.
            </span>
            <span className="gateway-neon-button gateway-continue">
              Continue <Icon name="arrow-right" size={28} />
            </span>
          </button>
          <div className="gateway-route-grid">
            {routes.map(route => (
              <button type="button" key={route.title} className="gateway-route-card" onClick={route.onClick}>
                <span className="gateway-glow-icon">
                  <Icon name={route.icon} size={34} />
                </span>
                <strong>
                  <span>{route.title.split(" ")[0]} </span>
                  <span>{route.title.split(" ").slice(1).join(" ")}</span>
                </strong>
                <span className="gateway-route-description">{route.detail}</span>
                <span className="gateway-route-cta">
                  <Icon name={route.actionIcon} size={16} />
                  <span>{route.action}</span>
                  <Icon name="arrow-right" size={17} />
                </span>
              </button>
            ))}
          </div>
        </section>
      </div>
      <div className="gateway-summary-row">{savedProgress}</div>
      <nav className="gateway-feature-strip" aria-label="Explore the Academy">
        {features.map(feature => (
          <a
            className={`gateway-feature gateway-feature-${feature.image}`}
            href={feature.href}
            key={feature.image}
          >
            <span className="gateway-feature-art" data-academy-effect="true" aria-hidden="true">
              <img src={`/images/academy/feature-${feature.image}-v2.webp`} width="160" height="110" alt="" />
              <span className="gateway-feature-orbit" />
              <AcademyParticles />
            </span>
            <span>
              <strong>{feature.title}</strong>
              <span>{feature.detail}</span>
            </span>
            <span className="gateway-feature-arrow">
              <Icon name="arrow-right" size={25} />
            </span>
          </a>
        ))}
      </nav>
      <footer className="gateway-footer">
        <details className="gateway-access-notes">
          <summary>About your Academy access</summary>
          {accessNotes}
        </details>
      </footer>
    </div>
  );
}
